import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  IconButton,
  Stack,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';


const BASE_TILE_HEIGHT = 60;
const TILE_BORDER_RADIUS = '6px';

function timeToMinutes(timeStr) {
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTimeLabel(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Strict Roman numeral validation
const romanRegex = /^(M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3}))$/;
// const romanRegex = /^[IVXLCDM]+$/i; // Less strict but lighter Roman numeral validation

export function prefixPeriod(value) {
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  const parts = trimmed.split(",").map(v => v.trim());
  const isRomanOrNumber = parts.every(v =>
    /^[0-9]+$/.test(v) || romanRegex.test(v.toUpperCase())
  );
  const result = isRomanOrNumber ? `Period: ${parts.join(", ")}` : trimmed;
  return result.slice(0, 20);
}


export function formatTimeToMinute(value) {
  if (!value) return "";

  // If it's a Date object
  if (value instanceof Date) {
    return value.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  // If it's a string like HH:MM:SS
  if (typeof value === "string") {
    return value.substring(0, 5);
  }

  return "";
}

function formatDateLabel(date) {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

export default function DailyTimeline({ timelineData }) {
  // Find "Today" index
  const todayIndex = useMemo(() => {
    const todayLabel = formatDateLabel(new Date());
    const idx = timelineData.findIndex(
      (day) => day.label === todayLabel
    );
    return idx >= 0 ? idx : 1; // fallback to middle
  }, [timelineData]);

  const [index, setIndex] = useState(todayIndex);

  const currentDay = timelineData[index];

  const schedule = useMemo(() => {
    if (!currentDay?.schedule?.length) return [];
    return currentDay.schedule
      .filter(task => task?.start_time && task?.end_time)
      .sort(
        (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
      );
  }, [currentDay]);

  const handlePrev = () => {
    if (index > 0) setIndex(index - 1);
  };

  const handleNext = () => {
    if (index < timelineData.length - 1) setIndex(index + 1);
  };

  const getTileHeight = (size = 1) => {
    const multiplier = Math.min(1 + 0.5 * (size - 1), 2);
    return BASE_TILE_HEIGHT * multiplier;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '12px',
        backgroundColor: 'white',
        boxShadow: 2,
        p: 3,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #E67E22, #E67E22dd)',
          transition: 'opacity 0.3s ease',
        }}
      />
      <Container
        maxWidth="xs"
        disableGutters
        sx={{
          px: 1,
          pb: 3,
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <IconButton
            fontSize="small"
            onClick={handlePrev} 
            disabled={index === 0}
            sx={{
              color: index === 0 ? 'text.disabled' : 'primary.main',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <NavigateBeforeOutlinedIcon fontSize="small" />
          </IconButton>

          {/* Center Content */}
          <Stack
            spacing={0.25}
            alignItems="center"
          >
            <Typography
              style={{
                fontWeight: 500,
                letterSpacing: '0.5px',
                fontSize: '0.75rem',
                color: '#64748b',
              }}
            >
              My Day
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {currentDay?.label}
            </Typography>
          </Stack>

          <IconButton
            fontSize="small"
            onClick={handleNext}
            disabled={index === timelineData.length - 1}
            sx={{
              color: index === timelineData.length - 1 ? 'text.disabled' : 'primary.main',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <NavigateNextOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Empty State */}
        {!schedule.length && (
          <Typography color="text.secondary" align="center">
            No tasks scheduled
          </Typography>
        )}

        {/* Schedule Cards */}
        {schedule.length > 0 && (
          <Box sx={{ position: 'relative' }}>
            <Stack spacing={1.5}>
              {schedule.map((task, i) => (
                <Paper
                  sx={{
                    height: getTileHeight(task.slot_size),
                    boxShadow: 0,
                    borderRadius: TILE_BORDER_RADIUS,
                    backgroundColor: task.color || '#fff',
                    flexDirection: 'row',
                    display: 'flex',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }
                  }}
                  // onClick={onClick}
                >
                  {/* Tile Text Content */}
                  <Box
                    sx={{ flexGrow: 1, py: 0.25, px: 1, alignContent: 'top', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                  >
                    {/* Time */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}>
                      <Typography
                        variant="body2"
                        fontWeight="400"
                        textAlign="left"
                        noWrap
                        sx={{ fontSize: '0.67rem' }}
                      >
                        {formatTimeToMinute(task.start_time)} - {formatTimeToMinute(task.end_time)}
                      </Typography>
                      {task.is_substitute && (
                        <IconButton onClick={() => null} size="small" sx={{ p: 0}}>
                          <TransferWithinAStationIcon color='primary' sx={{ fontSize: '0.75rem'  }} />
                        </IconButton>
                      )}
                      <Chip
                        // key={key}
                        label={prefixPeriod(task.time_label)}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 16,
                          minWidth: 32,
                          fontSize: '0.67rem',
                          borderColor: '#cccccc',
                          // background: '#dddddd',
                          borderRadius: 1, // This makes it rectangular with rounded corners
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    </Box>
                    
                    {/* Subject */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography
                          variant="body2"
                          fontWeight="400"
                          textAlign="left"
                          noWrap
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {task.is_substitute ? `Substitute - ${task.subject}` : task.subject}
                        </Typography>
                    </Box>

                    {/* Section and Classroom */}
                    {/* <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 0.25, alignItems: 'flex-start', pl: 0.25, mb: 0.25 }}> */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, mt: 'auto' }}>
                      {/* Left: Section */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          ml: 0.25,
                          gap: 0.3,
                        }}
                      >
                        <IconButton onClick={() => null} size="small" sx={{ p: 0}}>
                          <GroupsOutlinedIcon color='primary' sx={{ fontSize: '0.75rem'  }} />
                        </IconButton>
                        <Typography
                          variant="body2"
                          fontWeight="400"
                          textAlign="left"
                          noWrap
                          sx={{ fontSize: '0.67rem', ml: 0.25 }}
                        >
                          {task.section}
                        </Typography>
                      </Box>

                      {/* Right: Location */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.3,
                        }}
                      >
                        <IconButton onClick={() => null} size="small" sx={{ p: 0}}>
                          <PlaceOutlinedIcon color='primary' sx={{ fontSize: '0.75rem'  }} />
                        </IconButton>
                        <Typography
                          variant="body2"
                          fontWeight="400"
                          textAlign="left"
                          noWrap
                          sx={{ fontSize: '0.67rem', ml: 0.25 }}
                        >
                          {task.classroom}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
}
